import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AppModal from '../components/AppModal';
import api from '../services/api';
import {
  activarCaja,
  cancelarTraspaso,
  crearCaja,
  desactivarCaja,
  editarCaja,
  listarCajas,
  listarTraspasos,
  obtenerBalanzaCaja,
  obtenerMovimientosCaja,
  obtenerTraspaso,
  registrarAjuste,
  registrarIngreso,
  registrarSalida,
  registrarTraspaso
} from '../services/cajaService';
import { esAdministrador } from '../utils/usuarioActual';

const TIPOS_MOVIMIENTO = [
  '',
  'SALDO_INICIAL',
  'INGRESO',
  'SALIDA',
  'SALIDA_RECIBO',
  'REVERSA_RECIBO',
  'AJUSTE_POSITIVO',
  'AJUSTE_NEGATIVO',
  'TRASPASO_SALIDA',
  'TRASPASO_ENTRADA',
  'REVERSA_TRASPASO_SALIDA',
  'REVERSA_TRASPASO_ENTRADA'
];

const TIPOS_MANUALES = [
  ['INGRESO', 'Ingreso'],
  ['SALIDA', 'Egreso'],
  ['AJUSTE_POSITIVO', 'Ajuste positivo'],
  ['AJUSTE_NEGATIVO', 'Ajuste negativo']
];

const TIPOS_OPERACION_TRASPASO = [
  ['', 'Todas'],
  ['TRASPASO_MISMA_MONEDA', 'Traspaso misma moneda'],
  ['COMPRA_USD', 'Compra de dólares'],
  ['VENTA_USD', 'Venta de dólares']
];

const ETIQUETAS_TIPO = {
  SALDO_INICIAL: 'Saldo inicial',
  INGRESO: 'Ingreso',
  SALIDA: 'Egreso',
  SALIDA_RECIBO: 'Egreso por recibo',
  REVERSA_RECIBO: 'Reversa de recibo',
  AJUSTE_POSITIVO: 'Ajuste positivo',
  AJUSTE_NEGATIVO: 'Ajuste negativo',
  TRASPASO_SALIDA: 'Traspaso salida',
  TRASPASO_ENTRADA: 'Traspaso entrada',
  REVERSA_TRASPASO_SALIDA: 'Reversa traspaso salida',
  REVERSA_TRASPASO_ENTRADA: 'Reversa traspaso entrada'
};

const ETIQUETAS_OPERACION = {
  TRASPASO_MISMA_MONEDA: 'Traspaso misma moneda',
  COMPRA_USD: 'Compra de dólares',
  VENTA_USD: 'Venta de dólares'
};

function etiquetaMovimiento(tipo) {
  return ETIQUETAS_TIPO[tipo] || tipo || 'Todos';
}

function etiquetaOperacion(tipo) {
  return ETIQUETAS_OPERACION[tipo] || tipo || '-';
}

function formatoFecha(valor) {
  return String(valor || '').replace('T', ' ').slice(0, 19);
}

function formatoImporte(valor, moneda = 'MXN') {
  const textoMoneda = String(moneda || '').toUpperCase();
  const currency = textoMoneda.includes('USD') || textoMoneda.includes('DOLAR') || textoMoneda.includes('DÓLAR')
    ? 'USD'
    : 'MXN';

  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2
  }).format(Number(valor || 0));
}

function renderIngreso(item, moneda) {
  return item.EsEntrada ? formatoImporte(item.Monto, moneda) : '';
}

function renderEgreso(item, moneda) {
  return item.EsEntrada ? '' : formatoImporte(item.Monto, moneda);
}

function renderReferenciaMovimiento(item) {
  const texto = item.FolioRecibo || item.Referencia || '-';

  if (item.ReciboId) {
    return <Link to={`/recibos/${item.ReciboId}`}>{texto}</Link>;
  }

  return texto;
}

function esIdValido(valor) {
  const numero = Number(valor);
  return Number.isInteger(numero) && numero > 0;
}

function normalizarTexto(valor) {
  return String(valor || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();
}

function claveMoneda(caja) {
  const texto = normalizarTexto(`${caja?.Moneda || ''} ${caja?.MonedaNombre || ''} ${caja?.NombreMoneda || ''} ${caja?.CodigoMoneda || ''} ${caja?.DescripcionMoneda || ''}`);

  if (texto.includes('USD') || texto.includes('DOLAR') || texto.includes('DOLARES')) return 'USD';
  if (texto.includes('MXN') || texto.includes('MN') || texto.includes('MONEDA NACIONAL') || texto.includes('PESO') || texto.includes('PESOS')) return 'MXN';

  return '';
}

function calcularTraspasoUI(cajaOrigen, cajaDestino, montoOrigenTexto, tipoCambioTexto) {
  const montoOrigen = Number(montoOrigenTexto || 0);

  if (!cajaOrigen || !cajaDestino) {
    return { valido: false, mensaje: 'Seleccione caja origen y destino.', montoOrigen, montoDestino: 0, tipoCambio: 1, requiereTipoCambio: false };
  }

  if (String(cajaOrigen.Id) === String(cajaDestino.Id)) {
    return { valido: false, mensaje: 'La caja origen y destino deben ser diferentes.', montoOrigen, montoDestino: 0, tipoCambio: 1, requiereTipoCambio: false };
  }

  if (!Number.isFinite(montoOrigen) || montoOrigen <= 0) {
    return { valido: false, mensaje: 'Capture un monto origen mayor a cero.', montoOrigen, montoDestino: 0, tipoCambio: 1, requiereTipoCambio: false };
  }

  if (Number(cajaOrigen.MonedaId) === Number(cajaDestino.MonedaId)) {
    return { valido: true, tipoOperacion: 'TRASPASO_MISMA_MONEDA', etiqueta: 'Traspaso misma moneda', montoOrigen, montoDestino: Number(montoOrigen.toFixed(2)), tipoCambio: 1, requiereTipoCambio: false };
  }

  const origen = claveMoneda(cajaOrigen);
  const destino = claveMoneda(cajaDestino);
  const tipoCambio = Number(tipoCambioTexto || 0);

  if (!origen || !destino) {
    return { valido: false, mensaje: 'No fue posible identificar la moneda de las cajas.', montoOrigen, montoDestino: 0, tipoCambio, requiereTipoCambio: true };
  }

  if (!Number.isFinite(tipoCambio) || tipoCambio <= 0) {
    return { valido: false, mensaje: 'Capture el tipo de cambio MXN por USD.', montoOrigen, montoDestino: 0, tipoCambio, requiereTipoCambio: true };
  }

  if (origen === 'MXN' && destino === 'USD') {
    return { valido: true, tipoOperacion: 'COMPRA_USD', etiqueta: 'Compra de dólares', montoOrigen, montoDestino: Number((montoOrigen / tipoCambio).toFixed(2)), tipoCambio: Number(tipoCambio.toFixed(6)), requiereTipoCambio: true };
  }

  if (origen === 'USD' && destino === 'MXN') {
    return { valido: true, tipoOperacion: 'VENTA_USD', etiqueta: 'Venta de dólares', montoOrigen, montoDestino: Number((montoOrigen * tipoCambio).toFixed(2)), tipoCambio: Number(tipoCambio.toFixed(6)), requiereTipoCambio: true };
  }

  return { valido: false, mensaje: 'Solo se permiten traspasos entre la misma moneda o entre MXN y USD.', montoOrigen, montoDestino: 0, tipoCambio, requiereTipoCambio: true };
}

function CajaPage() {
  const admin = esAdministrador();
  const [tab, setTab] = useState('cajas');
  const [cajas, setCajas] = useState([]);
  const [catalogos, setCatalogos] = useState([]);
  const [cajaSeleccionadaId, setCajaSeleccionadaId] = useState('');
  const [movimientos, setMovimientos] = useState([]);
  const [paginacion, setPaginacion] = useState({ paginaActual: 1, totalPaginas: 0, totalRegistros: 0, pageSize: 25 });
  const [traspasos, setTraspasos] = useState([]);
  const [paginacionTraspasos, setPaginacionTraspasos] = useState({ paginaActual: 1, totalPaginas: 0, totalRegistros: 0, pageSize: 25 });
  const [balanza, setBalanza] = useState(null);
  const [mensaje, setMensaje] = useState('');
  const [aceptandoModal, setAceptandoModal] = useState(false);
  const [modal, setModal] = useState({ abierto: false, mensaje: '', variante: 'info', tipo: 'info' });

  const [formCaja, setFormCaja] = useState({
    Id: null,
    Nombre: '',
    Descripcion: '',
    MonedaId: '',
    SaldoInicial: '',
    Activa: true,
    PermitirSaldoNegativo: false
  });

  const [formMovimiento, setFormMovimiento] = useState({
    tipo: 'INGRESO',
    Monto: '',
    Concepto: '',
    Referencia: '',
    Descripcion: ''
  });

  const [formTraspaso, setFormTraspaso] = useState({
    cajaOrigenId: '',
    cajaDestinoId: '',
    montoOrigen: '',
    tipoCambio: '',
    concepto: '',
    referencia: '',
    descripcion: ''
  });

  const [filtrosMov, setFiltrosMov] = useState({
    fechaInicio: '',
    fechaFin: '',
    tipoMovimiento: '',
    folio: '',
    page: 1,
    pageSize: 25
  });

  const [filtrosTraspasos, setFiltrosTraspasos] = useState({
    cajaId: '',
    fechaInicio: '',
    fechaFin: '',
    tipoOperacion: '',
    folio: '',
    page: 1,
    pageSize: 25
  });

  const [filtrosBalanza, setFiltrosBalanza] = useState({
    fechaInicio: '',
    fechaFin: ''
  });

  const monedas = useMemo(
    () => catalogos.filter(item => item.Tipo === 'Moneda' && item.Activo),
    [catalogos]
  );

  const cajasActivas = useMemo(
    () => cajas.filter(item => item.Activa),
    [cajas]
  );

  const cajaSeleccionada = useMemo(
    () => cajas.find(item => String(item.Id) === String(cajaSeleccionadaId)),
    [cajas, cajaSeleccionadaId]
  );

  const cajaOrigenTraspaso = useMemo(
    () => cajas.find(item => String(item.Id) === String(formTraspaso.cajaOrigenId)),
    [cajas, formTraspaso.cajaOrigenId]
  );

  const cajaDestinoTraspaso = useMemo(
    () => cajas.find(item => String(item.Id) === String(formTraspaso.cajaDestinoId)),
    [cajas, formTraspaso.cajaDestinoId]
  );

  const [calculoTraspaso, setCalculoTraspaso] = useState({
    valido: false,
    mensaje: 'Seleccione caja origen y destino.',
    montoOrigen: 0,
    montoDestino: 0,
    tipoCambio: 1,
    requiereTipoCambio: false
  });

  const saldoPosteriorOrigen = cajaOrigenTraspaso
    ? Number(cajaOrigenTraspaso.SaldoActual || 0) - Number(calculoTraspaso.montoOrigen || 0)
    : 0;
  const saldoPosteriorDestino = cajaDestinoTraspaso
    ? Number(cajaDestinoTraspaso.SaldoActual || 0) + Number(calculoTraspaso.montoDestino || 0)
    : 0;
  const saldoInsuficienteTraspaso = Boolean(
    cajaOrigenTraspaso &&
    calculoTraspaso.montoOrigen > 0 &&
    saldoPosteriorOrigen < 0 &&
    !cajaOrigenTraspaso.PermitirSaldoNegativo
  );

  useEffect(() => {
    setCalculoTraspaso(calcularTraspasoUI(cajaOrigenTraspaso, cajaDestinoTraspaso, formTraspaso.montoOrigen, formTraspaso.tipoCambio));
  }, [cajaOrigenTraspaso, cajaDestinoTraspaso, formTraspaso.montoOrigen, formTraspaso.tipoCambio, cajas]);

  useEffect(() => {
    cargarInicial();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (esIdValido(cajaSeleccionadaId)) {
      cargarMovimientos(1);
      cargarBalanza();
    } else {
      setMovimientos([]);
      setBalanza(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cajaSeleccionadaId]);

  async function cargarInicial() {
    try {
      const [cajasData, catRes] = await Promise.all([
        listarCajas(),
        api.get('/catalogos?incluirInactivos=1')
      ]);

      setCajas(cajasData);
      setCatalogos(catRes.data || []);

      if (cajasData.length > 0) {
        setCajaSeleccionadaId(prev => prev || String(cajasData[0].Id));
      }

      await cargarTraspasos(1);
    } catch (error) {
      mostrarError(error.response?.data?.mensaje || error.message);
    }
  }

  function mostrarError(texto) {
    setModal({ abierto: true, mensaje: texto, variante: 'warning', tipo: 'info' });
  }

  function mostrarInfo(titulo, texto, variante = 'info') {
    setModal({ abierto: true, titulo, mensaje: texto, variante, tipo: 'info' });
  }

  function cerrarModal() {
    if (aceptandoModal) return;
    setModal({ abierto: false, mensaje: '', variante: 'info', tipo: 'info' });
  }

  function limpiarCaja() {
    setFormCaja({
      Id: null,
      Nombre: '',
      Descripcion: '',
        MonedaId: '',
      SaldoInicial: '',
      Activa: true,
      PermitirSaldoNegativo: false
    });
  }

  function limpiarTraspaso() {
    setFormTraspaso({
      cajaOrigenId: '',
      cajaDestinoId: '',
      montoOrigen: '',
      tipoCambio: '',
      concepto: '',
      referencia: '',
      descripcion: ''
    });
  }

  function editarCajaSeleccionada(caja) {
    if (!esIdValido(caja?.Id)) return;

    setFormCaja({
      Id: caja.Id,
      Nombre: caja.Nombre || '',
      Descripcion: caja.Descripcion || '',
      MonedaId: caja.MonedaId ? String(caja.MonedaId) : '',
      SaldoInicial: '',
      Activa: Boolean(caja.Activa),
      PermitirSaldoNegativo: Boolean(caja.PermitirSaldoNegativo)
    });
    setTab('cajas');
  }

  async function guardarCaja(e) {
    e.preventDefault();

    if (!admin) return;

    try {
      setMensaje('');

      const payload = {
        ...formCaja,
        MonedaId: Number(formCaja.MonedaId),
        SaldoInicial: Number(formCaja.SaldoInicial || 0)
      };

      if (formCaja.Id) {
        await editarCaja(formCaja.Id, payload);
        setMensaje('Caja actualizada correctamente.');
      } else {
        await crearCaja(payload);
        setMensaje('Caja creada correctamente.');
      }

      limpiarCaja();
      await cargarInicial();
    } catch (error) {
      mostrarError(error.response?.data?.mensaje || error.message);
    }
  }

  async function cambiarEstadoCaja(caja, activa) {
    if (!admin || !esIdValido(caja?.Id)) return;

    try {
      if (activa) {
        await activarCaja(caja.Id);
      } else {
        await desactivarCaja(caja.Id);
      }

      setMensaje(activa ? 'Caja activada.' : 'Caja desactivada.');
      await cargarInicial();
    } catch (error) {
      mostrarError(error.response?.data?.mensaje || error.message);
    }
  }

  async function cargarMovimientos(page = filtrosMov.page) {
    if (!esIdValido(cajaSeleccionadaId)) {
      setMovimientos([]);
      setPaginacion({ paginaActual: 1, totalPaginas: 0, totalRegistros: 0, pageSize: filtrosMov.pageSize });
      return;
    }

    try {
      const data = await obtenerMovimientosCaja(cajaSeleccionadaId, {
        ...filtrosMov,
        page
      });

      setMovimientos(data.movimientos || []);
      setPaginacion(data.paginacion || { paginaActual: 1, totalPaginas: 0, totalRegistros: 0, pageSize: 25 });
      setFiltrosMov(prev => ({ ...prev, page }));
    } catch (error) {
      mostrarError(error.response?.data?.mensaje || error.message);
    }
  }

  async function guardarMovimiento(e) {
    e.preventDefault();

    if (!admin || !esIdValido(cajaSeleccionadaId)) return;

    try {
      const payload = {
        Monto: Number(formMovimiento.Monto),
        Concepto: formMovimiento.Concepto,
        Referencia: formMovimiento.Referencia || null,
        Descripcion: formMovimiento.Descripcion || null
      };

      if (formMovimiento.tipo === 'INGRESO') {
        await registrarIngreso(cajaSeleccionadaId, payload);
      } else if (formMovimiento.tipo === 'SALIDA') {
        await registrarSalida(cajaSeleccionadaId, payload);
      } else {
        await registrarAjuste(cajaSeleccionadaId, {
          ...payload,
          TipoMovimiento: formMovimiento.tipo
        });
      }

      setFormMovimiento({ tipo: 'INGRESO', Monto: '', Concepto: '', Referencia: '', Descripcion: '' });
      setMensaje('Movimiento registrado correctamente.');
      await cargarInicial();
      await cargarMovimientos(1);
      await cargarBalanza();
    } catch (error) {
      mostrarError(error.response?.data?.mensaje || error.message);
    }
  }


  async function cargarTraspasos(page = filtrosTraspasos.page) {
    try {
      const data = await listarTraspasos({
        ...filtrosTraspasos,
        cajaId: esIdValido(filtrosTraspasos.cajaId) ? Number(filtrosTraspasos.cajaId) : '',
        page
      });

      setTraspasos(data.traspasos || []);
      setPaginacionTraspasos(data.paginacion || { paginaActual: 1, totalPaginas: 0, totalRegistros: 0, pageSize: 25 });
      setFiltrosTraspasos(prev => ({ ...prev, page }));
    } catch (error) {
      mostrarError(error.response?.data?.mensaje || error.message);
    }
  }

  async function guardarTraspaso(e) {
    e.preventDefault();

    if (!admin) return;

    if (!esIdValido(formTraspaso.cajaOrigenId) || !esIdValido(formTraspaso.cajaDestinoId)) {
      mostrarError('Seleccione caja origen y destino.');
      return;
    }

    if (!calculoTraspaso.valido) {
      mostrarError(calculoTraspaso.mensaje);
      return;
    }

    if (saldoInsuficienteTraspaso) {
      mostrarError('Saldo insuficiente en la caja origen.');
      return;
    }

    if (!formTraspaso.concepto.trim()) {
      mostrarError('El concepto es obligatorio.');
      return;
    }

    try {
      await registrarTraspaso({
        cajaOrigenId: Number(formTraspaso.cajaOrigenId),
        cajaDestinoId: Number(formTraspaso.cajaDestinoId),
        montoOrigen: Number(formTraspaso.montoOrigen),
        tipoCambio: calculoTraspaso.tipoCambio,
        concepto: formTraspaso.concepto,
        referencia: formTraspaso.referencia || null,
        descripcion: formTraspaso.descripcion || null
      });

      limpiarTraspaso();
      setMensaje('Traspaso registrado correctamente.');
      await cargarInicial();
      await cargarTraspasos(1);
      if (esIdValido(cajaSeleccionadaId)) {
        await cargarMovimientos(1);
        await cargarBalanza();
      }
    } catch (error) {
      mostrarError(error.response?.data?.mensaje || error.message);
    }
  }

  async function verDetalleTraspaso(id) {
    if (!esIdValido(id)) return;

    try {
      const data = await obtenerTraspaso(id);
      const t = data.traspaso;
      const texto = [
        `Traspaso #${t.Id}: ${t.CajaOrigen} -> ${t.CajaDestino}`,
        `${etiquetaOperacion(t.TipoOperacion)} | TC ${Number(t.TipoCambio || 0).toFixed(6)}`,
        `Origen: ${formatoImporte(t.MontoOrigen, t.MonedaOrigen)} | Destino: ${formatoImporte(t.MontoDestino, t.MonedaDestino)}`,
        `Concepto: ${t.Concepto}`,
        `Referencia: ${t.Referencia || '-'}`,
        `Movimientos ligados: ${(data.movimientos || []).length}`
      ].join('\n');

      mostrarInfo('Detalle de traspaso', texto, t.Cancelado ? 'warning' : 'info');
    } catch (error) {
      mostrarError(error.response?.data?.mensaje || error.message);
    }
  }

  function solicitarCancelarTraspaso(traspaso) {
    if (!esIdValido(traspaso?.Id)) return;

    setModal({
      abierto: true,
      titulo: 'Cancelar traspaso',
      mensaje: `Se generarán reversas contables para el traspaso #${traspaso.Id}. No se borrarán movimientos.`,
      variante: 'danger',
      tipo: 'cancelarTraspaso',
      traspaso
    });
  }

  async function ejecutarCancelarTraspaso(motivo) {
    if (modal.tipo !== 'cancelarTraspaso' || !modal.traspaso || !esIdValido(modal.traspaso.Id)) return;

    try {
      setAceptandoModal(true);
      await cancelarTraspaso(modal.traspaso.Id, { motivo });
      setModal({ abierto: false, mensaje: '', variante: 'info', tipo: 'info' });
      setMensaje('Traspaso cancelado con reversa contable.');
      await cargarInicial();
      await cargarTraspasos(1);
      if (esIdValido(cajaSeleccionadaId)) {
        await cargarMovimientos(1);
        await cargarBalanza();
      }
    } catch (error) {
      mostrarError(error.response?.data?.mensaje || error.message);
    } finally {
      setAceptandoModal(false);
    }
  }
  async function cargarBalanza() {
    if (!esIdValido(cajaSeleccionadaId)) {
      setBalanza(null);
      return;
    }

    try {
      const data = await obtenerBalanzaCaja(cajaSeleccionadaId, filtrosBalanza);
      setBalanza(data);
    } catch (error) {
      mostrarError(error.response?.data?.mensaje || error.message);
    }
  }

  return (
    <div className="page-container-normal">
      <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <div>
          <h1>Caja</h1>
          <div className="text-muted">Control local de efectivo y balanza por caja</div>
        </div>
      </div>

      {mensaje && <div className="alert alert-info">{mensaje}</div>}

      <div className="d-flex gap-2 mb-3 flex-wrap">
        {[
          ['cajas', 'Cajas'],
          ['movimientos', 'Movimientos'],
          ['traspasos', 'Traspasos'],
          ['balanza', 'Balanza']
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={tab === value ? 'btn btn-primary' : 'btn btn-outline-secondary'}
            onClick={() => setTab(value)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'cajas' && (
        <>
          {admin && (
            <div className="card-box mb-3">
              <h5>{formCaja.Id ? 'Editar caja' : 'Nueva caja'}</h5>
              <form onSubmit={guardarCaja}>
                <div className="row g-2 align-items-end">
                  <div className="col-md-3">
                    <label className="form-label">Nombre</label>
                    <input className="form-control" value={formCaja.Nombre} onChange={e => setFormCaja(prev => ({ ...prev, Nombre: e.target.value }))} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Descripción</label>
                    <input className="form-control" value={formCaja.Descripcion} onChange={e => setFormCaja(prev => ({ ...prev, Descripcion: e.target.value }))} />
                  </div>

                  <div className="col-md-2">
                    <label className="form-label">Moneda</label>
                    <select className="form-select" value={formCaja.MonedaId} disabled={Boolean(formCaja.Id)} onChange={e => setFormCaja(prev => ({ ...prev, MonedaId: e.target.value }))}>
                      <option value="">Seleccione</option>
                      {monedas.map(item => <option key={item.Id} value={item.Id}>{item.Valor}</option>)}
                    </select>
                  </div>
                  {!formCaja.Id && (
                    <div className="col-md-2">
                      <label className="form-label">Saldo inicial</label>
                      <input type="number" step="0.01" className="form-control money-input" value={formCaja.SaldoInicial} onChange={e => setFormCaja(prev => ({ ...prev, SaldoInicial: e.target.value }))} />
                    </div>
                  )}
                  <div className="col-md-3 d-flex gap-3 align-items-center">
                    <label className="form-check-label">
                      <input className="form-check-input me-2" type="checkbox" checked={formCaja.PermitirSaldoNegativo} onChange={e => setFormCaja(prev => ({ ...prev, PermitirSaldoNegativo: e.target.checked }))} />
                      Permitir saldo negativo
                    </label>
                  </div>
                  <div className="col-md-3 d-flex gap-2">
                    <button className="btn btn-primary" type="submit">Guardar</button>
                    <button className="btn btn-outline-secondary" type="button" onClick={limpiarCaja}>Limpiar</button>
                  </div>
                </div>
              </form>
            </div>
          )}

          <div className="card-box">
            <h5>Cajas</h5>
            <div className="table-wrap">
              <table className="table table-sm table-hover align-middle">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Moneda</th>
                    <th className="money-header">Saldo actual</th>
                    <th>Activa</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {cajas.map(caja => (
                    <tr key={caja.Id}>
                      <td>{caja.Nombre}</td>
                      <td>{caja.Moneda || caja.MonedaId}</td>
                      <td className="money-cell">{formatoImporte(caja.SaldoActual, caja.Moneda)}</td>
                      <td>{caja.Activa ? 'Sí' : 'No'}</td>
                      <td>
                        <div className="d-flex gap-1 flex-wrap">
                          <button className="btn btn-sm btn-outline-primary" type="button" onClick={() => { setCajaSeleccionadaId(String(caja.Id)); setTab('movimientos'); }}>Movimientos</button>
                          {admin && <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => editarCajaSeleccionada(caja)}>Editar</button>}
                          {admin && caja.Activa && <button className="btn btn-sm btn-outline-danger" type="button" onClick={() => cambiarEstadoCaja(caja, false)}>Desactivar</button>}
                          {admin && !caja.Activa && <button className="btn btn-sm btn-outline-success" type="button" onClick={() => cambiarEstadoCaja(caja, true)}>Activar</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {cajas.length === 0 && <tr><td colSpan="5" className="text-center text-muted py-3">Sin cajas registradas.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === 'movimientos' && (
        <div className="card-box">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <h5 className="mb-0">Movimientos</h5>
            <select className="form-select" style={{ maxWidth: '320px' }} value={cajaSeleccionadaId} onChange={e => setCajaSeleccionadaId(e.target.value)}>
              <option value="">Seleccione caja</option>
              {cajas.map(caja => <option key={caja.Id} value={caja.Id}>{caja.Nombre} - {formatoImporte(caja.SaldoActual, caja.Moneda)}</option>)}
            </select>
          </div>

          {!esIdValido(cajaSeleccionadaId) && <div className="alert alert-light border">Seleccione una caja para consultar movimientos.</div>}

          {admin && cajaSeleccionada && (
            <form onSubmit={guardarMovimiento} className="mb-3">
              <div className="row g-2 align-items-end">
                <div className="col-md-2">
                  <label className="form-label">Tipo</label>
                  <select className="form-select" value={formMovimiento.tipo} onChange={e => setFormMovimiento(prev => ({ ...prev, tipo: e.target.value }))}>
                    {TIPOS_MANUALES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Monto</label>
                  <input type="number" step="0.01" className="form-control" value={formMovimiento.Monto} onChange={e => setFormMovimiento(prev => ({ ...prev, Monto: e.target.value }))} required />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Concepto</label>
                  <input className="form-control" value={formMovimiento.Concepto} onChange={e => setFormMovimiento(prev => ({ ...prev, Concepto: e.target.value }))} required />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Referencia</label>
                  <input className="form-control" value={formMovimiento.Referencia} onChange={e => setFormMovimiento(prev => ({ ...prev, Referencia: e.target.value }))} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Descripción</label>
                  <input className="form-control" value={formMovimiento.Descripcion} onChange={e => setFormMovimiento(prev => ({ ...prev, Descripcion: e.target.value }))} />
                </div>
                <div className="col-md-2">
                  <button className="btn btn-primary w-100" type="submit">Registrar</button>
                </div>
              </div>
            </form>
          )}

          <div className="row g-2 align-items-end mb-3">
            <div className="col-md-2"><label className="form-label">Fecha inicio</label><input type="date" className="form-control" value={filtrosMov.fechaInicio} onChange={e => setFiltrosMov(prev => ({ ...prev, fechaInicio: e.target.value }))} /></div>
            <div className="col-md-2"><label className="form-label">Fecha fin</label><input type="date" className="form-control" value={filtrosMov.fechaFin} onChange={e => setFiltrosMov(prev => ({ ...prev, fechaFin: e.target.value }))} /></div>
            <div className="col-md-2"><label className="form-label">Tipo</label><select className="form-select" value={filtrosMov.tipoMovimiento} onChange={e => setFiltrosMov(prev => ({ ...prev, tipoMovimiento: e.target.value }))}>{TIPOS_MOVIMIENTO.map(tipo => <option key={tipo || 'todos'} value={tipo}>{etiquetaMovimiento(tipo)}</option>)}</select></div>
            <div className="col-md-2"><label className="form-label">Folio</label><input className="form-control" value={filtrosMov.folio} onChange={e => setFiltrosMov(prev => ({ ...prev, folio: e.target.value }))} /></div>
            <div className="col-md-2"><button className="btn btn-outline-primary w-100" type="button" onClick={() => cargarMovimientos(1)}>Buscar</button></div>
          </div>

          <div className="table-wrap">
            <table className="table table-sm table-hover align-middle">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Beneficiario</th>
                  <th>Concepto</th>
                  <th>Referencia</th>
                  <th className="money-header">Ingreso</th>
                  <th className="money-header">Egreso</th>
                  <th className="money-header">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {movimientos.map(item => (
                  <tr key={item.Id}>
                    <td>{formatoFecha(item.FechaMovimiento)}</td>
                    <td>{etiquetaMovimiento(item.TipoMovimiento)}</td>
                    <td>{item.Beneficiario || item.PersonaDestino || '-'}</td>
                    <td>{item.Concepto}</td>
                    <td>{renderReferenciaMovimiento(item)}</td>
                    <td className="money-cell">{renderIngreso(item, cajaSeleccionada?.Moneda)}</td>
                    <td className="money-cell">{renderEgreso(item, cajaSeleccionada?.Moneda)}</td>
                    <td className="money-cell">{formatoImporte(item.SaldoPosterior, cajaSeleccionada?.Moneda)}</td>
                  </tr>
                ))}
                {movimientos.length === 0 && <tr><td colSpan="8" className="text-center text-muted py-3">Sin movimientos.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2"><span className="text-muted small">{paginacion.totalRegistros} movimientos</span><div className="d-flex gap-2"><button className="btn btn-outline-secondary btn-sm" disabled={paginacion.paginaActual <= 1} onClick={() => cargarMovimientos(paginacion.paginaActual - 1)}>Anterior</button><span className="small text-muted">Página {paginacion.paginaActual} de {paginacion.totalPaginas}</span><button className="btn btn-outline-secondary btn-sm" disabled={paginacion.paginaActual >= paginacion.totalPaginas} onClick={() => cargarMovimientos(paginacion.paginaActual + 1)}>Siguiente</button></div></div>
        </div>
      )}

      {tab === 'traspasos' && (
        <div className="card-box">
          <h5>Traspasos</h5>

          {admin && (
            <form onSubmit={guardarTraspaso} className="mb-3">
              <div className="row g-2 align-items-end">
                <div className="col-md-3"><label className="form-label">Caja origen</label><select className="form-select" value={formTraspaso.cajaOrigenId} onChange={e => setFormTraspaso(prev => ({ ...prev, cajaOrigenId: e.target.value }))} required><option value="">Seleccione</option>{cajasActivas.map(caja => <option key={caja.Id} value={caja.Id}>{caja.Nombre} - {caja.Moneda}</option>)}</select></div>
                <div className="col-md-3"><label className="form-label">Caja destino</label><select className="form-select" value={formTraspaso.cajaDestinoId} onChange={e => setFormTraspaso(prev => ({ ...prev, cajaDestinoId: e.target.value }))} required><option value="">Seleccione</option>{cajasActivas.map(caja => <option key={caja.Id} value={caja.Id}>{caja.Nombre} - {caja.Moneda}</option>)}</select></div>
                <div className="col-md-2"><label className="form-label">Monto origen</label><input type="number" step="0.01" className="form-control money-input" value={formTraspaso.montoOrigen} onChange={e => setFormTraspaso(prev => ({ ...prev, montoOrigen: e.target.value }))} required /></div>
                <div className="col-md-2"><label className="form-label">Tipo de cambio <span title="TC = MXN por 1 USD. Ej. 17.50">(?)</span></label><input type="number" step="0.000001" className="form-control money-input" title="TC = MXN por 1 USD. Ej. 17.50" value={calculoTraspaso.requiereTipoCambio ? formTraspaso.tipoCambio : '1'} disabled={!calculoTraspaso.requiereTipoCambio} onChange={e => setFormTraspaso(prev => ({ ...prev, tipoCambio: e.target.value }))} /></div>
                <div className="col-md-2"><label className="form-label">Tipo operación</label><input className="form-control" value={calculoTraspaso.etiqueta || '-'} disabled readOnly /></div>
                <div className="col-md-2"><label className="form-label">Monto destino</label><input className="form-control money-input" value={calculoTraspaso.valido ? formatoImporte(calculoTraspaso.montoDestino, cajaDestinoTraspaso?.Moneda) : '-'} disabled readOnly /></div>
                <div className="col-md-3"><label className="form-label">Concepto</label><input className="form-control" value={formTraspaso.concepto} onChange={e => setFormTraspaso(prev => ({ ...prev, concepto: e.target.value }))} required /></div>
                <div className="col-md-2"><label className="form-label">Referencia</label><input className="form-control" value={formTraspaso.referencia} onChange={e => setFormTraspaso(prev => ({ ...prev, referencia: e.target.value }))} /></div>
                <div className="col-md-3"><label className="form-label">Descripción</label><input className="form-control" value={formTraspaso.descripcion} onChange={e => setFormTraspaso(prev => ({ ...prev, descripcion: e.target.value }))} /></div>
                <div className="col-md-2"><button className="btn btn-primary w-100" type="submit" disabled={!esIdValido(formTraspaso.cajaOrigenId) || !esIdValido(formTraspaso.cajaDestinoId) || !calculoTraspaso.valido || saldoInsuficienteTraspaso || !formTraspaso.concepto.trim() || !(calculoTraspaso.montoDestino > 0)}>Registrar traspaso</button></div>
              </div>
              <div className="row g-2 mt-2">
                <div className="col-md-3"><div className="border rounded p-2 bg-white"><div className="text-muted small">Saldo origen</div><strong className="money-value d-block">{formatoImporte(cajaOrigenTraspaso?.SaldoActual, cajaOrigenTraspaso?.Moneda)}</strong></div></div>
                <div className="col-md-3"><div className="border rounded p-2 bg-white"><div className="text-muted small">Saldo posterior origen</div><strong className={saldoInsuficienteTraspaso ? 'money-value d-block text-danger' : 'money-value d-block'}>{formatoImporte(saldoPosteriorOrigen, cajaOrigenTraspaso?.Moneda)}</strong></div></div>
                <div className="col-md-3"><div className="border rounded p-2 bg-white"><div className="text-muted small">Saldo posterior destino</div><strong className="money-value d-block">{formatoImporte(saldoPosteriorDestino, cajaDestinoTraspaso?.Moneda)}</strong></div></div>
                <div className="col-md-3"><div className="border rounded p-2 bg-white"><div className="text-muted small">Validación</div><strong>{saldoInsuficienteTraspaso ? 'Saldo insuficiente' : (calculoTraspaso.valido ? 'Listo' : calculoTraspaso.mensaje)}</strong></div></div>
              </div>
            </form>
          )}

          <div className="row g-2 align-items-end mb-3">
            <div className="col-md-2"><label className="form-label">Caja</label><select className="form-select" value={filtrosTraspasos.cajaId} onChange={e => setFiltrosTraspasos(prev => ({ ...prev, cajaId: e.target.value }))}><option value="">Todas</option>{cajas.map(caja => <option key={caja.Id} value={caja.Id}>{caja.Nombre}</option>)}</select></div>
            <div className="col-md-2"><label className="form-label">Fecha inicio</label><input type="date" className="form-control" value={filtrosTraspasos.fechaInicio} onChange={e => setFiltrosTraspasos(prev => ({ ...prev, fechaInicio: e.target.value }))} /></div>
            <div className="col-md-2"><label className="form-label">Fecha fin</label><input type="date" className="form-control" value={filtrosTraspasos.fechaFin} onChange={e => setFiltrosTraspasos(prev => ({ ...prev, fechaFin: e.target.value }))} /></div>
            <div className="col-md-2"><label className="form-label">Operación</label><select className="form-select" value={filtrosTraspasos.tipoOperacion} onChange={e => setFiltrosTraspasos(prev => ({ ...prev, tipoOperacion: e.target.value }))}>{TIPOS_OPERACION_TRASPASO.map(([value, label]) => <option key={value || 'todas'} value={value}>{label}</option>)}</select></div>
            <div className="col-md-2"><label className="form-label">Referencia</label><input className="form-control" value={filtrosTraspasos.folio} onChange={e => setFiltrosTraspasos(prev => ({ ...prev, folio: e.target.value }))} /></div>
            <div className="col-md-2"><button className="btn btn-outline-primary w-100" type="button" onClick={() => cargarTraspasos(1)}>Buscar</button></div>
          </div>

          <div className="table-wrap">
            <table className="table table-sm table-hover align-middle">
              <thead><tr><th>Fecha</th><th>Origen</th><th>Destino</th><th>Tipo operación</th><th className="money-header">Monto origen</th><th className="money-header">TC</th><th className="money-header">Monto destino</th><th>Concepto</th><th>Referencia</th><th>Usuario</th><th>Estatus</th><th>Acciones</th></tr></thead>
              <tbody>
                {traspasos.map(item => (
                  <tr key={item.Id}>
                    <td>{formatoFecha(item.FechaTraspaso)}</td><td>{item.CajaOrigen}</td><td>{item.CajaDestino}</td><td>{etiquetaOperacion(item.TipoOperacion)}</td>
                    <td className="money-cell">{formatoImporte(item.MontoOrigen, item.MonedaOrigen)}</td><td className="money-cell">{Number(item.TipoCambio || 0).toFixed(6)}</td><td className="money-cell">{formatoImporte(item.MontoDestino, item.MonedaDestino)}</td>
                    <td>{item.Concepto}</td><td>{item.Referencia || '-'}</td><td>{item.UsuarioCreacion || '-'}</td><td>{item.Cancelado ? 'Cancelado' : 'Activo'}</td>
                    <td><div className="d-flex gap-1 flex-wrap"><button className="btn btn-sm btn-outline-primary" type="button" onClick={() => verDetalleTraspaso(item.Id)}>Detalle</button>{admin && !item.Cancelado && <button className="btn btn-sm btn-outline-danger" type="button" onClick={() => solicitarCancelarTraspaso(item)}>Cancelar</button>}</div></td>
                  </tr>
                ))}
                {traspasos.length === 0 && <tr><td colSpan="12" className="text-center text-muted py-3">Sin traspasos.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2"><span className="text-muted small">{paginacionTraspasos.totalRegistros} traspasos</span><div className="d-flex gap-2"><button className="btn btn-outline-secondary btn-sm" disabled={paginacionTraspasos.paginaActual <= 1} onClick={() => cargarTraspasos(paginacionTraspasos.paginaActual - 1)}>Anterior</button><span className="small text-muted">Página {paginacionTraspasos.paginaActual} de {paginacionTraspasos.totalPaginas}</span><button className="btn btn-outline-secondary btn-sm" disabled={paginacionTraspasos.paginaActual >= paginacionTraspasos.totalPaginas} onClick={() => cargarTraspasos(paginacionTraspasos.paginaActual + 1)}>Siguiente</button></div></div>
        </div>
      )}
      {tab === 'balanza' && (
        <div className="card-box">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <h5 className="mb-0">Balanza</h5>
            <select className="form-select" style={{ maxWidth: '320px' }} value={cajaSeleccionadaId} onChange={e => setCajaSeleccionadaId(e.target.value)}>
              <option value="">Seleccione caja</option>
              {cajas.map(caja => <option key={caja.Id} value={caja.Id}>{caja.Nombre}</option>)}
            </select>
          </div>
          {!esIdValido(cajaSeleccionadaId) && <div className="alert alert-light border">Seleccione una caja para consultar balanza.</div>}
          <div className="row g-2 align-items-end mb-3">
            <div className="col-md-2"><label className="form-label">Fecha inicio</label><input type="date" className="form-control" value={filtrosBalanza.fechaInicio} onChange={e => setFiltrosBalanza(prev => ({ ...prev, fechaInicio: e.target.value }))} /></div>
            <div className="col-md-2"><label className="form-label">Fecha fin</label><input type="date" className="form-control" value={filtrosBalanza.fechaFin} onChange={e => setFiltrosBalanza(prev => ({ ...prev, fechaFin: e.target.value }))} /></div>
            <div className="col-md-2"><button className="btn btn-outline-primary w-100" type="button" onClick={cargarBalanza}>Actualizar</button></div>
          </div>
          {balanza && (
            <>
              <div className="row g-2 mb-3">
                {[
                  ['Saldo inicial', balanza.saldoInicialPeriodo],
                  ['Ingresos', balanza.totalIngresos],
                  ['Egresos manuales', balanza.totalSalidasManuales],
                  ['Egresos por recibos', balanza.totalSalidasRecibos],
                  ['Traspasos entrada', balanza.totalTraspasosEntrada],
                  ['Traspasos salida', balanza.totalTraspasosSalida],
                  ['Reversas recibos', balanza.totalReversasRecibos],
                  ['Reversas traspasos', balanza.totalReversasTraspasos],
                  ['Ajustes positivos', balanza.totalAjustesPositivos],
                  ['Ajustes negativos', balanza.totalAjustesNegativos],
                  ['Saldo final', balanza.saldoFinalPeriodo],
                  ['Saldo actual', balanza.saldoActualCaja]
                ].map(([label, value]) => <div className="col-md-3" key={label}><div className="border rounded p-2 bg-white"><div className="text-muted small">{label}</div><strong className="money-value d-block">{formatoImporte(value, balanza.moneda)}</strong></div></div>)}
              </div>
              <div className="table-wrap">
                <table className="table table-sm table-hover align-middle">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Tipo</th>
                      <th>Beneficiario</th>
                      <th>Concepto</th>
                      <th>Referencia</th>
                      <th className="money-header">Ingreso</th>
                      <th className="money-header">Egreso</th>
                      <th className="money-header">Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(balanza.movimientos || []).map(item => (
                      <tr key={item.Id}>
                        <td>{formatoFecha(item.FechaMovimiento)}</td>
                        <td>{etiquetaMovimiento(item.TipoMovimiento)}</td>
                        <td>{item.Beneficiario || item.PersonaDestino || '-'}</td>
                        <td>{item.Concepto}</td>
                        <td>{renderReferenciaMovimiento(item)}</td>
                        <td className="money-cell">{renderIngreso(item, balanza.moneda)}</td>
                        <td className="money-cell">{renderEgreso(item, balanza.moneda)}</td>
                        <td className="money-cell">{formatoImporte(item.SaldoPosterior, balanza.moneda)}</td>
                      </tr>
                    ))}
                    {(balanza.movimientos || []).length === 0 && <tr><td colSpan="8" className="text-center text-muted py-3">Sin movimientos.</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      <AppModal
        abierto={modal.abierto}
        titulo={modal.titulo || 'Caja'}
        mensaje={modal.mensaje}
        variante={modal.variante}
        mostrarInput={modal.tipo === 'cancelarTraspaso'}
        inputLabel="Motivo de cancelación"
        inputPlaceholder="Motivo opcional"
        mostrarCancelar={modal.tipo === 'cancelarTraspaso'}
        textoAceptar={modal.tipo === 'cancelarTraspaso' ? 'Cancelar traspaso' : 'Aceptar'}
        aceptando={aceptandoModal}
        onCancelar={cerrarModal}
        onAceptar={modal.tipo === 'cancelarTraspaso' ? ejecutarCancelarTraspaso : cerrarModal}
      />
    </div>
  );
}

export default CajaPage;




















